// Admin Q&A Page Component
// Provides WYSIWYG admin interface for Q&A management
// Mimics main page layout with search and CRUD operations

import React, { useState, useEffect, useCallback } from 'react';
import { RevoltButton } from '../../../../components/RevoltButton';
import { SearchBar, QAItem } from '../../components';
import { QAModal } from './QAModal';
import { QADeleteConfirmModal } from '../../../workspace/components/QADeleteConfirmModal';
import './AdminQAPage.css';
import '../../LearnSection.css';

export function AdminQAPage() {
  const [qaItems, setQaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedQA, setSelectedQA] = useState(null);

  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [qaToDelete, setQaToDelete] = useState(null);
  const [isDeletingQA, setIsDeletingQA] = useState(false);

  // Load Q&A items
  const loadQAItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/learn/admin/questions', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load Q&A items');
      }

      const data = await response.json();
      setQaItems(data.qa || []);
    } catch (error) {
      console.error('Failed to load Q&A items:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadQAItems();
  }, [loadQAItems]);

  // Handle create Q&A
  const handleCreateQA = () => {
    setModalMode('create');
    setSelectedQA(null);
    setIsModalOpen(true);
  };

  // Handle edit Q&A
  const handleEditQA = (qa) => {
    setModalMode('edit');
    setSelectedQA(qa);
    setIsModalOpen(true);
  };

  // Handle delete Q&A
  const handleDeleteQA = (qa) => {
    setQaToDelete(qa);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmQADelete = async () => {
    if (!qaToDelete) return;

    setIsDeletingQA(true);
    try {
      const response = await fetch(`/api/learn/admin/questions/${qaToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete Q&A');
      }

      // Remove from local state
      setQaItems(qaItems.filter(qa => qa.id !== qaToDelete.id));
      setIsDeleteModalOpen(false);
      setQaToDelete(null);

    } catch (error) {
      console.error('Failed to delete Q&A:', error);
      alert('Failed to delete Q&A. Please try again.');
      setIsDeleteModalOpen(false);
      setQaToDelete(null);
    } finally {
      setIsDeletingQA(false);
    }
  };

  // Handle modal submit
  const handleModalSubmit = async (qaData) => {
    try {
      let response;
      let updatedQA;

      if (modalMode === 'create') {
        response = await fetch('/api/learn/admin/questions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(qaData)
        });
      } else {
        response = await fetch(`/api/learn/admin/questions/${selectedQA.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(qaData)
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to ${modalMode} Q&A`);
      }

      const data = await response.json();
      updatedQA = data.qa;

      // Update local state
      if (modalMode === 'create') {
        setQaItems([updatedQA, ...qaItems]);
      } else {
        setQaItems(qaItems.map(qa => qa.id === updatedQA.id ? updatedQA : qa));
      }
    } catch (error) {
      console.error(`Failed to ${modalMode} Q&A:`, error);
      throw error; // Re-throw to let modal handle it
    }
  };

  // Filter Q&A items based on search
  // - Regular search: searches question titles only
  // - #tag search: searches tags (e.g., "#general" searches for tag "general")
  const filteredQAItems = qaItems.filter(qa => {
    if (!searchTerm) return true;

    const search = searchTerm.trim();
    const searchLower = search.toLowerCase();

    // Check if this is a tag search (starts with #)
    if (search.startsWith('#')) {
      const tagSearch = searchLower.substring(1); // Remove the #
      return qa.tags && qa.tags.some(tag => tag.toLowerCase().includes(tagSearch));
    }

    // Regular search: only search question titles
    return qa.question.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <div className="admin-tab-content">
        <div className="admin-section-header">
          <h2>Questions & Answers Management</h2>
          <p>Manage Q&A content and categories.</p>
        </div>
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading Q&A items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-tab-content">
        <div className="admin-section-header">
          <h2>Questions & Answers Management</h2>
          <p>Manage Q&A content and categories.</p>
        </div>
        <div className="admin-error">
          <i className="fa-solid fa-exclamation-triangle"></i>
          <h3>Error Loading Q&A</h3>
          <p>{error}</p>
          <RevoltButton onClick={loadQAItems} variant="primary">
            Try Again
          </RevoltButton>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-tab-content">
      {/* Header with Create Button */}
      <div className="admin-section-header admin-section-header--with-button">
        <div className="admin-section-header-left">
          <h2>Questions & Answers Management</h2>
          <p>Manage Q&A content and categories.</p>
        </div>
        <div className="admin-section-header-right">
          <RevoltButton
            variant="primary"
            size="medium"
            onClick={handleCreateQA}
          >
            <i className="fa-solid fa-plus"></i>
            Create Q&A
          </RevoltButton>
        </div>
      </div>

      {/* Search Bar */}
      <div className="admin-qa-search">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search Q&A items..."
        />
      </div>

      {/* Q&A Items List - WYSIWYG Style */}
      <div className="admin-qa-content">
        <div className="learn-featured-column">
          <div className="learn-column-header">
            <h2>Questions & Answers</h2>
            <span className="qa-count">
              {filteredQAItems.length} item{filteredQAItems.length !== 1 ? 's' : ''}
              {searchTerm && ` (filtered from ${qaItems.length})`}
            </span>
          </div>

          <div className="learn-featured-items">
            {filteredQAItems.length === 0 ? (
              <div className="admin-qa-empty">
                <i className="fa-solid fa-question-circle"></i>
                <h3>No Q&A items found</h3>
                <p>
                  {searchTerm
                    ? `No Q&A items match "${searchTerm}"`
                    : 'Get started by creating your first Q&A item.'
                  }
                </p>
              </div>
            ) : (
              filteredQAItems.map((qa) => (
                <QAItem
                  key={qa.id}
                  qa={qa}
                  isAdmin={true}
                  onEdit={handleEditQA}
                  onDelete={handleDeleteQA}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Q&A Modal */}
      <QAModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={modalMode}
        initialData={selectedQA}
        onSubmit={handleModalSubmit}
      />

      {/* Q&A Delete Confirmation Modal */}
      <QADeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isDeletingQA) {
            setIsDeleteModalOpen(false);
            setQaToDelete(null);
          }
        }}
        onConfirm={handleConfirmQADelete}
        qa={qaToDelete}
        isLoading={isDeletingQA}
      />
    </div>
  );
}